import { useState, useEffect, useContext } from "react";
import TwoFactorSetup, { BackupCodes } from "./TwoFactorSetup";
import { api } from "../api";
import { setUsdRate } from "../useUsdRate";
import { ThemeContext, THEMES } from "../theme";

if (typeof document !== "undefined" && !document.getElementById("rbxwar-settings-responsive")) {
  const st = document.createElement("style");
  st.id = "rbxwar-settings-responsive";
  st.textContent = `
    .settings-row:last-child {
      border-bottom: none !important;
    }
    @media (max-width: 1100px) {
      .settings-row {
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 8px !important;
      }
      .settings-row > div:last-child {
        width: 100% !important;
      }
      .settings-row input,
      .settings-row select {
        width: 100% !important;
        box-sizing: border-box !important;
      }
    }
    @media (max-width: 768px) {
      .settings-header-row {
        flex-direction: column !important;
        align-items: stretch !important;
        gap: 10px !important;
      }
      .settings-header-row button {
        width: 100% !important;
      }
    }
  `;
  document.head.appendChild(st);
}

const TIMEZONES = [
  { value: "Pacific/Midway",      label: "(GMT-11:00) Midway Island" },
  { value: "Pacific/Honolulu",    label: "(GMT-10:00) Hawaii" },
  { value: "America/Anchorage",   label: "(GMT-9:00) Alaska" },
  { value: "America/Los_Angeles", label: "(GMT-8:00) Pacific Time (US & Canada)" },
  { value: "America/Denver",      label: "(GMT-7:00) Mountain Time (US & Canada)" },
  { value: "America/Chicago",     label: "(GMT-6:00) Central Time (US & Canada)" },
  { value: "America/New_York",    label: "(GMT-5:00) Eastern Time (US & Canada)" },
  { value: "America/Halifax",     label: "(GMT-4:00) Atlantic Time (Canada)" },
  { value: "America/Sao_Paulo",   label: "(GMT-3:00) Brasilia" },
  { value: "Atlantic/Azores",     label: "(GMT-1:00) Azores" },
  { value: "Europe/London",       label: "(GMT+0:00) London, Dublin" },
  { value: "Europe/Paris",        label: "(GMT+1:00) Paris, Berlin, Madrid" },
  { value: "Europe/Athens",       label: "(GMT+2:00) Athens, Cairo" },
  { value: "Europe/Moscow",       label: "(GMT+3:00) Moscow, Istanbul" },
  { value: "Asia/Dubai",          label: "(GMT+4:00) Dubai" },
  { value: "Asia/Karachi",        label: "(GMT+5:00) Karachi" },
  { value: "Asia/Dhaka",          label: "(GMT+6:00) Dhaka" },
  { value: "Asia/Bangkok",        label: "(GMT+7:00) Bangkok, Jakarta" },
  { value: "Asia/Shanghai",       label: "(GMT+8:00) Beijing, Singapore" },
  { value: "Asia/Tokyo",          label: "(GMT+9:00) Tokyo, Seoul" },
  { value: "Australia/Sydney",    label: "(GMT+10:00) Sydney, Melbourne" },
  { value: "Pacific/Auckland",    label: "(GMT+12:00) Auckland" },
];

const DEFAULTS = {
  AutoResumeBot: true,
  InventoryScanMethod: "auto",
  TradeNotifyWinningOnly: true,
  FloorAlertIntervalMinutes: 60,
  Timezone: "America/Los_Angeles",
  ItemDelay: 10,
  PriceWarDelay: 30,
  RapFloorPercent: 90,
  MinPriceFloor: 500,
  USDRatePer1k: 5,
  AutodiscoverLoops: 10,
  ListValuedItems: false,
  ValuedListPercent: 100,
  ValuedItemCap: 100000,
  ValuedTier1Cap: 60000,
  ValuedTier1Floor: 100,
  ValuedTier3Min: 600000,
  ValuedTier3Floor: 100,
  FloorDecayEnabled: false,
  SpiralResetThreshold: 20,
  FloorDecayDays: 2,
  FloorDecayRate: 1.5,
  ShowSellers: false,
  ShowDebug: false,
  ShowScannedMessages: true,
  DiscordUserId: "",
  Webhooks: { Sales: "", PriceUpdate: "", SalesPrivate: "", LoopComplete: "", DailyRecap: "", Trades: "", Notifications: "" }
};

export default function Settings({ token, tier, limits, theme, onThemeChange }) {
  const themeCtx = useContext(ThemeContext);
  const accent = themeCtx.accent;

  const [cfg, setCfg]         = useState(DEFAULTS);
  const [saved, setSaved]     = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [backfillMsg, setBackfillMsg]     = useState("");
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [profileName, setProfileName]     = useState("");
  const [profileNameMsg, setProfileNameMsg] = useState("");

  const [savedName, setSavedName] = useState("");
  useEffect(() => {
    api.getDisplayName(token).then(r => {
      const n = r.display_name || "";
      setProfileName(n);
      setSavedName(n);
    }).catch(() => {});
  }, []);

  const saveProfileName = async () => {
    const n = profileName.trim();
    if (!/^[A-Za-z0-9_. -]{3,15}$/.test(n)) {
      setProfileNameMsg("Failed: use 3-15 characters: letters, numbers, spaces, _ . -");
      return;
    }
    try {
      const res = await api.setDisplayName(token, n);
      const saved = res.display_name || n;
      setProfileName(saved);
      setSavedName(saved);
      setProfileNameMsg("✓ Saved");
      setTimeout(() => setProfileNameMsg(""), 2500);
    } catch (e) {
      if (e.sessionExpired) return;
      setProfileNameMsg(e.message === "USERNAME_TAKEN" ? "Failed: that username is taken" : `Failed: ${e.message}`);
    }
  };

  const resetBackfill = async () => {
    if (!window.confirm("This forces a fresh 30-day sale history backfill on your next bot restart. Continue?")) return;
    setBackfillLoading(true);
    setBackfillMsg("");
    try {
      const res = await api.resetBackfill(token);
      setBackfillMsg(
        res.cleared.length > 0
          ? `Cleared for ${res.cleared.length} account(s). Restart your bot to trigger a fresh backfill.`
          : "Nothing was flagged as backfilled yet. Restarting your bot should already attempt it."
      );
    } catch (e) {
      setBackfillMsg(`Failed: ${e.message}`);
    } finally {
      setBackfillLoading(false);
    }
  };

  // ── Danger Zone handlers ──────────────────────────────────────────────
  const [clearing, setClearing] = useState(""); // "accounts" | "proxies" | "presets" | ""
  const [clearMsg, setClearMsg] = useState("");

  const clearAll = async (kind) => {
    const labels = {
      accounts: "every Roblox account",
      proxies: "every saved proxy",
      presets: "every applied preset (this undoes what they set: floors, not-selling flags, etc.)",
    };
    const confirmed = window.confirm(
      `This will permanently delete ${labels[kind]}. This can't be undone. Are you sure?`
    );
    if (!confirmed) return;

    setClearing(kind);
    setClearMsg("");
    try {
      if (kind === "accounts") await api.clearAccounts(token);
      if (kind === "proxies") await api.clearProxies(token);
      if (kind === "presets") await api.clearPresets(token);
      setClearMsg(`✓ Cleared ${kind}. Refresh the page to see the change everywhere.`);
    } catch (e) {
      setClearMsg(`✗ Failed to clear ${kind}: ${e.message}`);
    } finally {
      setClearing("");
    }
  };

  const [loadErr, setLoadErr] = useState("");
  const loadConfig = () => {
    setLoadErr(""); setLoading(true);
    api.getConfig(token).then(c => {
      if (c && Object.keys(c).length) setCfg({ ...DEFAULTS, ...c });
      setLoading(false);
    }).catch(e => {
      if (e.sessionExpired) { window.dispatchEvent(new Event("rbxwar:session-expired")); return; }
      setLoadErr(e.message || "Could not reach the server");
      setLoading(false);
    });
  };
  useEffect(() => { loadConfig(); }, []);

  const set   = (key, val) => setCfg(prev => ({ ...prev, [key]: val }));
  const setWh = (key, val) => setCfg(prev => ({ ...prev, Webhooks: { ...prev.Webhooks, [key]: val } }));

  const save = async () => {
    setSaving(true); setSaveErr(""); setSaved(false);
    try {
      const { accounts, Accounts, id, user_id, ...cleanCfg } = cfg;
      await api.saveConfig(token, cleanCfg);
      setUsdRate(token, cfg.USDRatePer1k);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveErr(e.message || "Save failed. Check console");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: "#555", padding: 40 }}>Loading settings...</div>;
  if (loadErr) return (
    <div style={{ padding: 40, color: "#f87171", fontSize: 14 }}>
      Couldn't load your settings ({loadErr}). Your saved config was not touched.
      <div><button style={{ ...styles.btn, marginTop: 14, background: accent }} onClick={loadConfig}>Retry</button></div>
    </div>
  );

  const hasAutodiscover = limits?.autodiscover ?? false;
  const hasValuedItems  = hasAutodiscover || limits?.maxItems !== 5;
  const hasFloorDecay   = hasAutodiscover;

  const SECTION_LIST = ["Account", "Bot", "Pricing", "Notifications", "Display", "Advanced"];
  const scrollToSection = (title) => {
    const el = document.getElementById(`settings-section-${slugify(title)}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
      <div style={styles.sideNav}>
        <div style={styles.sideNavTitle}>Jump to</div>
        {SECTION_LIST.map(title => (
          <button key={title} style={styles.sideNavItem} onClick={() => scrollToSection(title)}>
            {title}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {(saved || saveErr) && (
          <div style={{
            position: "fixed", top: 20, right: 20, zIndex: 9999,
            background: saved ? "#14532d" : "#7f1d1d",
            border: `1px solid ${saved ? accent : "#ef4444"}`,
            color: saved ? accent : "#fca5a5",
            borderRadius: 10, padding: "14px 20px",
            fontSize: 14, fontWeight: 600,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            {saved ? "✓ Settings saved!" : `✗ ${saveErr}`}
          </div>
        )}

        <div className="settings-header-row" style={styles.header}>
          <h2 style={styles.title}>Settings</h2>
          <button
            style={{ ...styles.btn, background: saving ? "#166534" : saved ? "#166534" : accent, opacity: saving ? 0.7 : 1 }}
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : saved ? "✓ Saved!" : "Save Settings"}
          </button>
        </div>

        <Section title="ACCOUNT">
          <Row label="Username" tip="3-15 characters. Support uses this to find you in logs and tickets, so it has to be unique.">
            <div style={{ display: "flex", gap: 8 }}>
              <input
                style={{ ...styles.textInput, width: 150 }}
                maxLength={15}
                value={profileName}
                onChange={e => setProfileName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && saveProfileName()}
                placeholder="Choose a username"
              />
              <button
                style={{ ...styles.btn, background: accent, opacity: profileName.trim() === savedName ? 0.4 : 1 }}
                onClick={saveProfileName}
                disabled={profileName.trim() === savedName}
              >
                Save
              </button>
            </div>
          </Row>
          {profileNameMsg && (
            <div style={{ color: profileNameMsg.startsWith("✓") ? "#4ade80" : "#f87171", fontSize: 12, margin: "8px 0 4px" }}>{profileNameMsg}</div>
          )}
          <div style={{ color: "#e5e5e5", fontSize: 13.5, fontWeight: 500, margin: "22px 0 4px" }}>Password &amp; 2FA</div>
          <div style={{ color: "#555", fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>Two-factor authentication for your login.</div>
          <AccountSecurity token={token} accent={accent} />
        </Section>

        <Section title="BOT">
          <Row label="How autodiscover scans your accounts" tip="Auto uses Roblox's tradable-items endpoint plus a classic supplement. For large inventories (100+ items), Classic Only is significantly faster and more reliable, just note it can't find face items.">
            <div style={{ display:"flex", flexDirection:"column", gap:8, width:340, maxWidth:"100%" }}>
              <select
                style={{ ...styles.textInput, width:"100%" }}
                value={cfg.InventoryScanMethod || "auto"}
                onChange={e => set("InventoryScanMethod", e.target.value)}
              >
                <option value="auto">Auto: Tradable Items + Classic supplement</option>
                <option value="tradableitems">Tradable Items Only</option>
                <option value="classic">Classic Only: recommended for large inventories (100+ items)</option>
              </select>
              <div style={{ fontSize:11.5, color:"#555", lineHeight:1.6, padding:"8px 12px", background:"#111", borderRadius:6, border:"1px solid #1a1a1a" }}>
                💡 <span style={{ color:"#888" }}>If you have a large inventory (100+ limiteds), <strong style={{ color:"#ccc" }}>Classic Only</strong> is faster and picks up more items. The only tradeoff is it can't detect face limiteds, if you sell faces, use Auto instead.</span>
              </div>
            </div>
          </Row>
          <Row label="Auto-resume bot after updates" tip="If your bot was running when we push a platform update or the server restarts, automatically start it back up instead of leaving it off until you notice">
            <Toggle value={cfg.AutoResumeBot} onChange={v => set("AutoResumeBot", v)} accent={accent} />
          </Row>
          <Row label="Item delay (s)" tip={hasAutodiscover ? "How long the bot waits between checking each item. Fastest speed allows down to 5s" : "How long the bot waits between checking each item. Medium speed floors at 10s. Upgrade to Pro for Fastest speeds"}>
            <NumInput value={cfg.ItemDelay} onChange={v => set("ItemDelay", v)} min={hasAutodiscover ? 5 : 10} />
          </Row>
          <Row label="Loop delay (s)" tip={hasAutodiscover ? "How long the bot waits after a full pass. Fastest speed allows down to 15s" : "How long the bot waits after a full pass. Medium speed floors at 30s. Upgrade to Pro for Fastest speeds"}>
            <NumInput value={cfg.PriceWarDelay} onChange={v => set("PriceWarDelay", v)} min={hasAutodiscover ? 15 : 30} />
          </Row>
          <Row label="Autodiscover every X loops" tip="How often the bot re-scans your inventory for newly acquired items. Pro only" disabled={!hasAutodiscover}>
            <NumInput value={cfg.AutodiscoverLoops} onChange={v => set("AutodiscoverLoops", v)} disabled={!hasAutodiscover} />
          </Row>
        </Section>

        <Section title="PRICING">
          <Row label="RAP floor %" tip="The bot will never price an item below this percent of its RAP (Recent Average Price)">
            <NumInput value={cfg.RapFloorPercent} onChange={v => set("RapFloorPercent", v)} suffix="%" />
          </Row>
          <Row label="Min price floor (R)" tip="Absolute lowest price in Robux the bot will ever list an item for, no matter what. Roblox's own minimum is 95, so anything lower is treated as 95">
            <NumInput value={cfg.MinPriceFloor} onChange={v => set("MinPriceFloor", v)} />
          </Row>
          <Row label="USD rate per 1k Robux" tip="Used to show your earnings in USD on the dashboard. Purely cosmetic, doesn't affect pricing">
            <NumInput value={cfg.USDRatePer1k} onChange={v => set("USDRatePer1k", v)} step={0.1} suffix="$" />
          </Row>
          <Group title="Valued items" locked={!hasValuedItems}>
          <Row label="List valued items" tip="Whether the bot prices items that have a Rolimons value, not just RAP" disabled={!hasValuedItems}>
            <Toggle value={cfg.ListValuedItems} onChange={v => set("ListValuedItems", v)} disabled={!hasValuedItems} accent={accent} />
          </Row>
          <Row label="Skip items valued over" tip="Items worth more than this get left alone entirely" disabled={!hasValuedItems}>
            <NumInput value={cfg.ValuedItemCap} onChange={v => set("ValuedItemCap", v)} disabled={!hasValuedItems} />
          </Row>
          <Row label="Tier 1 cap (pricewar below this)" tip="Valued items under this amount use the Tier 1 floor below" disabled={!hasValuedItems}>
            <NumInput value={cfg.ValuedTier1Cap} onChange={v => set("ValuedTier1Cap", v)} disabled={!hasValuedItems} />
          </Row>
          <Row label="Tier 1 floor %" tip="Minimum price (as % of value) for lower-tier valued items" disabled={!hasValuedItems}>
            <NumInput value={cfg.ValuedTier1Floor} onChange={v => set("ValuedTier1Floor", v)} suffix="%" disabled={!hasValuedItems} />
          </Row>
          <Row label="Tier 2 floor % (mid-range items)" tip="Minimum price (as % of value) for items between Tier 1 and Tier 3" disabled={!hasValuedItems}>
            <NumInput value={cfg.ValuedListPercent} onChange={v => set("ValuedListPercent", v)} suffix="%" disabled={!hasValuedItems} />
          </Row>
          <Row label="Tier 3 threshold (high value)" tip="Items worth more than this use the Tier 3 floor below" disabled={!hasValuedItems}>
            <NumInput value={cfg.ValuedTier3Min} onChange={v => set("ValuedTier3Min", v)} disabled={!hasValuedItems} />
          </Row>
          <Row label="Tier 3 floor %" tip="Minimum price (as % of value) for your highest-tier valued items" disabled={!hasValuedItems}>
            <NumInput value={cfg.ValuedTier3Floor} onChange={v => set("ValuedTier3Floor", v)} suffix="%" disabled={!hasValuedItems} />
          </Row>
          </Group>
          <Group title="Floor decay" locked={!hasFloorDecay}>
          <Row label="Enable floor decay" tip="Gradually lowers an item's price floor the longer it sits unsold. Pro only" disabled={!hasFloorDecay}>
            <Toggle value={cfg.FloorDecayEnabled} onChange={v => set("FloorDecayEnabled", v)} disabled={!hasFloorDecay} accent={accent} />
          </Row>
          <Row label="Start decaying after (days)" tip="How many days unsold before the floor starts dropping" disabled={!hasFloorDecay}>
            <NumInput value={cfg.FloorDecayDays} onChange={v => set("FloorDecayDays", v)} disabled={!hasFloorDecay} />
          </Row>
          <Row label="Decay rate per day" tip="How much the floor drops each additional day past the start threshold" disabled={!hasFloorDecay}>
            <NumInput value={cfg.FloorDecayRate} onChange={v => set("FloorDecayRate", v)} step={0.1} suffix="%" disabled={!hasFloorDecay} />
          </Row>
          </Group>
        </Section>

        <Section title="NOTIFICATIONS">
          <Row label="Your Discord User ID" tip="Right-click your name in Discord (Developer Mode on) → Copy User ID. Used to @mention you when something needs attention">
            <input style={styles.textInput} placeholder="e.g. 123456789012345678" value={cfg.DiscordUserId || ""} onChange={e => set("DiscordUserId", e.target.value)} />
          </Row>
          <Row label="Your timezone" tip="Used to time your Daily Recap for 11:59 PM your time, and to display Analytics dates/times correctly">
            <select style={styles.select} value={cfg.Timezone || "America/Los_Angeles"} onChange={e => set("Timezone", e.target.value)}>
              {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>
          </Row>
          <Row label="'Not best price' digest interval (min)" tip="How often you get one batched Discord message listing everything currently stuck behind a competitor's floor. 15 min minimum">
            <NumInput value={cfg.FloorAlertIntervalMinutes} onChange={v => set("FloorAlertIntervalMinutes", v)} min={15} suffix="min" />
          </Row>
          <Group title="Discord webhooks">
          {Object.keys(DEFAULTS.Webhooks).map(key => (
            <Row key={key} label={key} tip={key === "Notifications" ? "Pings you here when an account gets auto-removed (invalid cookie) or an item comes off hold" : key === "Trades" ? "Alerts you here for inbound trade offers" : undefined}>
              <input style={styles.textInput} placeholder="https://discord.com/api/webhooks/..." value={cfg.Webhooks?.[key] || ""} onChange={e => setWh(key, e.target.value)} />
            </Row>
          ))}
          <Row label="Only ping for trades you're winning" tip="On: only notified when their side is worth more than yours. Off: notified for every inbound trade">
            <Toggle value={cfg.TradeNotifyWinningOnly} onChange={v => set("TradeNotifyWinningOnly", v)} accent={accent} />
          </Row>
          </Group>
        </Section>

        <Section title="DISPLAY">
          <Row label="Show scan lines" tip="Log a line for every item the bot checks, even when no price change happens (already optimal, on hold, below floor, etc.). On by default so you can see the bot is actually working">
            <Toggle value={cfg.ShowScannedMessages} onChange={v => set("ShowScannedMessages", v)} accent={accent} />
          </Row>
          <Row label="Show seller names" tip="Show who else is reselling an item in the live logs">
            <Toggle value={cfg.ShowSellers} onChange={v => set("ShowSellers", v)} accent={accent} />
          </Row>
          <Row label="Show debug messages" tip="Include extra technical detail in the live logs">
            <Toggle value={cfg.ShowDebug} onChange={v => set("ShowDebug", v)} accent={accent} />
          </Row>
          <Row label="Bot battle threshold" tip="How many times the bot reprices the same item per hour before it suspects a bot war and pulls off sale for 3 loops to reset. 0 = disabled.">
            <NumInput value={cfg.SpiralResetThreshold} onChange={v => set("SpiralResetThreshold", v)} min={0} suffix="reprices/hr" />
          </Row>

          {/* ── Theme picker ──────────────────────────────────── */}
          <Row label="Dashboard theme" tip="Changes the accent colour across your entire dashboard and landing page">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {Object.values(THEMES).map(t => {
                const active = (theme || "mono") === t.name;
                const EMOJI = { blue: "◈", green: "◈", halloween: "🎃", kawaii: "🌸", mono: "◆" };
                return (
                  <button
                    key={t.name}
                    onClick={() => onThemeChange?.(t.name)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                      fontFamily: "inherit", fontSize: 12.5, fontWeight: active ? 700 : 400,
                      background: active ? t.accentBg : "#141414",
                      border: `1px solid ${active ? t.accent : "#222"}`,
                      color: active ? t.accent : "#666",
                      transition: "all 0.15s ease",
                      outline: "none",
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{EMOJI[t.name] || "◈"}</span>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.accent, flexShrink: 0 }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </Row>
        </Section>

        <Section title="ADVANCED">
          <Row label="Analytics/Recent Sales stuck at zero?" tip="The one-time 30-day sale history import only ever runs once per account. This forces it to try again on your next bot restart.">
            <button style={{ ...styles.btn, background: accent, opacity: backfillLoading ? 0.7 : 1 }} onClick={resetBackfill} disabled={backfillLoading}>
              {backfillLoading ? "..." : "Reset & retry backfill"}
            </button>
          </Row>
          {backfillMsg && <div style={{ color: "#888", fontSize: 12, marginTop: 8 }}>{backfillMsg}</div>}
          <Group title="Danger zone" tone="danger">
          <div style={{ color: "#f87171", fontSize: 11.5, marginBottom: 4, lineHeight: 1.6 }}>
            These actions are permanent and can't be undone. Consider stopping your bot first.
          </div>

          <Row label="Clear all accounts" tip="Removes every Roblox account from your bot. Stops the bot first if it's running.">
            <button
              style={{ ...styles.dangerBtn, opacity: clearing === "accounts" ? 0.6 : 1 }}
              onClick={() => clearAll("accounts")}
              disabled={!!clearing}
            >
              {clearing === "accounts" ? "Clearing..." : "Clear Accounts"}
            </button>
          </Row>

          <Row label="Clear all proxies" tip="Removes every saved proxy from your Proxy Pool.">
            <button
              style={{ ...styles.dangerBtn, opacity: clearing === "proxies" ? 0.6 : 1 }}
              onClick={() => clearAll("proxies")}
              disabled={!!clearing}
            >
              {clearing === "proxies" ? "Clearing..." : "Clear Proxies"}
            </button>
          </Row>

          <Row label="Clear all presets" tip="Un-applies every preset you've turned on (undoes floor overrides, not-selling flags, hard decay, etc.)">
            <button
              style={{ ...styles.dangerBtn, opacity: clearing === "presets" ? 0.6 : 1 }}
              onClick={() => clearAll("presets")}
              disabled={!!clearing}
            >
              {clearing === "presets" ? "Clearing..." : "Clear Presets"}
            </button>
          </Row>

          {clearMsg && <div style={{ color: clearMsg.startsWith("✓") ? accent : "#f87171", fontSize: 12, marginTop: 10 }}>{clearMsg}</div>}
          </Group>
        </Section>
      </div>
    </div>
  );
}

function AccountSecurity({ token, accent }) {
  const [st, setSt] = useState(null);
  const [mode, setMode] = useState(""); // "" | "password" | "codes"
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [fresh, setFresh] = useState(null);

  const refresh = () => api.getSecurityStatus(token).then(setSt).catch(() => {});
  useEffect(() => { refresh(); }, [token]);

  const reset = (m) => { setMode(m); setPw(""); setPw2(""); setCode(""); setErr(""); setMsg(""); setFresh(null); };
  const explain = (e) => (e.message === "WRONG_CODE" ? "That code isn't right." : e.message === "TOO_MANY_ATTEMPTS" ? "Too many wrong tries. Wait 5 minutes." : e.message);
  const inputStyle = { background: "#141414", border: "1px solid #222", borderRadius: 8, padding: "10px 14px", color: "#fff", fontSize: 13, outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box", marginBottom: 8 };
  const btn = { padding: "9px 16px", background: accent, border: "none", borderRadius: 8, color: "#000", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit" };
  const ghost = { padding: "9px 16px", background: "#141414", border: "1px solid #2a2a2a", borderRadius: 8, color: "#ccc", fontWeight: 600, cursor: "pointer", fontSize: 13, fontFamily: "inherit" };

  if (!st) return null;
  if (st.degraded) {
    return <div style={{ color: "#f59e0b", fontSize: 12.5, lineHeight: 1.6, maxWidth: 420 }}>Couldn't check your 2FA status right now, so nothing is shown as on. Try again in a minute.</div>;
  }
  if (!st.has_2fa) return <div style={{ maxWidth: 380 }}><TwoFactorSetup token={token} accent={accent} hasPassword={st.has_password} onDone={refresh} /></div>;
  const admin = !!st.is_admin_account;

  const changePassword = async () => {
    setErr(""); setMsg("");
    if (pw.length < 10) return setErr("Use at least 10 characters.");
    if (pw !== pw2) return setErr("Passwords don't match.");
    if (!code.trim()) return setErr("Enter a current 2FA code.");
    setBusy(true);
    try { await api.setPassword(token, pw, code.trim()); setMsg("Password changed."); setPw(""); setPw2(""); setCode(""); }
    catch (e) { if (!e.sessionExpired) setErr(explain(e)); } finally { setBusy(false); }
  };
  const newCodes = async () => {
    setErr(""); setMsg("");
    if (!/^\d{6}$/.test(code.trim())) return setErr("Enter the 6-digit code from your app.");
    setBusy(true);
    try { const r = await api.newBackupCodes(token, code.trim()); setFresh(r.backup_codes); setCode(""); refresh(); }
    catch (e) { if (!e.sessionExpired) setErr(explain(e)); } finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 380 }}>
      <div style={{ color: "#4ade80", fontSize: 12.5, marginBottom: 12 }}>
        &#10003; {admin ? "Admin key 2FA is on." : "Password and 2FA are on."} {st.backup_codes_left} backup code{st.backup_codes_left === 1 ? "" : "s"} left.
      </div>
      <div style={{ color: "#666", fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
        {admin
          ? "Your master key login asks for a 6-digit code, so the key alone can't get in."
          : "You log in with your username, password and a 6-digit code from your authenticator app."}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {!admin && <button style={mode === "password" ? btn : ghost} onClick={() => reset(mode === "password" ? "" : "password")}>Change password</button>}
        <button style={mode === "codes" ? btn : ghost} onClick={() => reset(mode === "codes" ? "" : "codes")}>New backup codes</button>
      </div>
      {mode === "password" && (
        <div>
          <input style={inputStyle} type="password" placeholder="New password (min 10 characters)" autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)} />
          <input style={inputStyle} type="password" placeholder="Confirm new password" autoComplete="new-password" value={pw2} onChange={e => setPw2(e.target.value)} />
          <input style={inputStyle} placeholder="Current 6-digit 2FA code" autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === "Enter" && changePassword()} />
          <button style={btn} onClick={changePassword} disabled={busy}>{busy ? "Saving..." : "Save new password"}</button>
        </div>
      )}
      {mode === "codes" && (
        fresh ? (
          <div>
            <div style={{ color: "#aaa", fontSize: 12.5, marginBottom: 10 }}>Your old codes no longer work. Save these now, they are shown once.</div>
            <BackupCodes codes={fresh} accent={accent} />
          </div>
        ) : (
          <div>
            <div style={{ color: "#777", fontSize: 12.5, marginBottom: 8 }}>This replaces all your backup codes. Enter a current code from your authenticator app.</div>
            <input style={inputStyle} inputMode="numeric" maxLength={6} placeholder="6-digit code" autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={e => e.key === "Enter" && newCodes()} />
            <button style={btn} onClick={newCodes} disabled={busy}>{busy ? "Working..." : "Make new codes"}</button>
          </div>
        )
      )}
      {err && <div style={{ color: "#f87171", fontSize: 12, marginTop: 10 }}>{err}</div>}
      {msg && <div style={{ color: "#4ade80", fontSize: 12, marginTop: 10 }}>{msg}</div>}
    </div>
  );
}

function slugify(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function Group({ title, locked, tone, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: "1px solid #161616", marginTop: 10 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", padding: "14px 0 6px", cursor: "pointer", fontFamily: "inherit", color: tone === "danger" ? "#f87171" : "#999", fontSize: 13, fontWeight: 600, textAlign: "left" }}
      >
        <span>
          {title}
          {locked && <span style={{ marginLeft: 10, fontSize: 10.5, color: "#f59e0b", fontWeight: 700, letterSpacing: 1 }}>🔒 UPGRADE TO UNLOCK</span>}
        </span>
        <span style={{ color: "#555", fontSize: 11 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ opacity: locked ? 0.45 : 1, pointerEvents: locked ? "none" : "auto" }}>{children}</div>}
    </div>
  );
}

function Section({ title, children, locked }) {
  return (
    <div id={`settings-section-${slugify(title)}`} style={{ background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 10, padding: "20px 24px", marginBottom: 12, position: "relative", opacity: locked ? 0.45 : 1, scrollMarginTop: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, paddingBottom: 12, borderBottom: "1px solid #1a1a1a" }}>
        <div style={{ color: "#444", fontSize: 10.5, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>{title}</div>
        {locked && <button style={styles.upgradeBtn} onClick={() => alert("Upgrade your plan to unlock this section!")}>🔒 Upgrade</button>}
      </div>
      <div style={{ pointerEvents: locked ? "none" : "auto" }}>{children}</div>
    </div>
  );
}

function Row({ label, tip, children, disabled }) {
  return (
    <div className="settings-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid #111", gap: 24, opacity: disabled ? 0.4 : 1 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#e5e5e5", fontSize: 13.5, fontWeight: 500, marginBottom: tip ? 4 : 0 }}>{label}</div>
        {tip && <div style={{ color: "#555", fontSize: 12, lineHeight: 1.5 }}>{tip}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function NumInput({ value, onChange, suffix, step = 1, min, disabled }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input
        style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 6, padding: "6px 10px", color: "#fff", fontSize: 13, width: 90, textAlign: "right", outline: "none" }}
        type="number" step={step} min={min} disabled={disabled} value={value}
        onChange={e => {
          let v = step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value);
          if (min !== undefined && !isNaN(v) && v < min) v = min;
          onChange(v);
        }}
      />
      {suffix && <span style={{ color: "#555", fontSize: 12 }}>{suffix}</span>}
    </div>
  );
}

function Toggle({ value, onChange, disabled, accent = "var(--rbx-accent, #38bdf8)" }) {
  return (
    <div
      onClick={() => !disabled && onChange(!value)}
      style={{ width: 42, height: 24, borderRadius: 12, background: value ? accent : "#222", cursor: disabled ? "not-allowed" : "pointer", position: "relative", transition: "background .2s" }}
    >
      <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: value ? 21 : 3, transition: "left .2s" }} />
    </div>
  );
}

const styles = {
  sideNav:      { width: 160, flexShrink: 0, position: "sticky", top: 20, background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 2 },
  sideNavTitle: { color: "#444", fontSize: 10, letterSpacing: 1, padding: "4px 8px 8px" },
  sideNavItem:  { textAlign: "left", background: "transparent", border: "none", color: "#888", fontSize: 12.5, padding: "7px 8px", borderRadius: 6, cursor: "pointer" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  title:        { color: "#fff", margin: 0, fontSize: 18 },
  btn:          { padding: "10px 20px", border: "none", borderRadius: 8, color: "#000", fontWeight: 700, cursor: "pointer", fontSize: 13 },
  textInput:    { background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 6, padding: "6px 10px", color: "#fff", fontSize: 12, width: 320, outline: "none", fontFamily: "monospace" },
  upgradeBtn:   { padding: "4px 12px", background: "#1a1a0a", border: "1px solid #a16207", borderRadius: 6, color: "#a16207", fontWeight: 700, cursor: "pointer", fontSize: 11 },
  dangerBtn:    { padding: "8px 16px", background: "#2a0f0f", border: "1px solid #7f1d1d", borderRadius: 8, color: "#f87171", fontWeight: 700, cursor: "pointer", fontSize: 12.5 },
  select:       { background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 6, padding: "6px 10px", color: "#fff", fontSize: 12, outline: "none", width: 240 },
};
